import { useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import type { LibraryResponse, OperatorStatusResponse, RunnerHealth } from "../shared/types";
import { getSession } from "./api";
import { api } from "./api";
import { Shell } from "./components/Shell";
import { UploadDrawer } from "./components/UploadDrawer";
import { AdDetailPage } from "./pages/AdDetailPage";
import { BrandDetailPage } from "./pages/BrandDetailPage";
import { BrandsPage } from "./pages/BrandsPage";
import { LibraryPage } from "./pages/LibraryPage";
import { LoginPage } from "./pages/LoginPage";
import { PlaceholderPage } from "./pages/PlaceholderPage";
import { ReviewsPage } from "./pages/ReviewsPage";
import { OperationsPage } from "./pages/OperationsPage";
import { SystemVisualPage } from "./pages/SystemVisualPage";
import { StylesPage } from "./pages/StylesPage";
import { StyleDetailPage } from "./pages/StyleDetailPage";
import { MakeAdPage } from "./pages/MakeAdPage";
import { ProductionKanbanPage } from "./pages/ProductionKanbanPage";
import { ProductionReviewPage } from "./pages/ProductionReviewPage";
import { ClientCalendarPage } from "./pages/ClientCalendarPage";
import { StrategyReviewDetailPage } from "./pages/StrategyReviewDetailPage";
import { StrategyReviewsPage } from "./pages/StrategyReviewsPage";
import { ScriptClarificationPage } from "./pages/ScriptClarificationPage";

export default function App() {
  const [session, setSession] = useState<"loading" | "authenticated" | "anonymous">("loading");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [reviewCount, setReviewCount] = useState(0);
  const [productionReviewCount, setProductionReviewCount] = useState(0);
  const [scriptClarificationCount, setScriptClarificationCount] = useState(0);
  const [attentionCount, setAttentionCount] = useState(0);
  const [runner, setRunner] = useState<RunnerHealth | null>(null);
  const [features, setFeatures] = useState<OperatorStatusResponse["features"] | null>(null);
  const [operatorStatusError, setOperatorStatusError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((value) => value + 1), []);
  const captureLibrary = useCallback((value: LibraryResponse) => { setReviewCount(value.openReviewCount); setRunner(value.runner); }, []);
  useEffect(() => { void getSession().then((value) => setSession(value.authenticated ? "authenticated" : "anonymous")); }, []);
  useEffect(() => {
    if (session !== "authenticated") return undefined;
    let active = true;
    const loadStatus = () => void api.status().then((value) => {
      if (!active) return;
      setReviewCount(value.openReviewCount);
      setProductionReviewCount(value.openProductionReviewCount);
      setScriptClarificationCount(value.scriptClarificationCount ?? 0);
      setAttentionCount(value.needsAttentionCount);
      setRunner(value.runner);
      setFeatures(value.features);
      setOperatorStatusError("");
    }).catch(() => setOperatorStatusError("Operator controls are temporarily unavailable. Retrying…"));
    loadStatus();
    const timer = window.setInterval(loadStatus, 15_000);
    return () => { active = false; window.clearInterval(timer); };
  }, [refreshKey, session]);

  if (session === "loading") return <div className="boot-screen"><span className="monogram">SD</span><p>Opening reference library…</p></div>;
  if (session === "anonymous") return <LoginPage onAuthenticated={() => setSession("authenticated")} />;
  if (features === null) return <div className="boot-screen"><span className="monogram">SD</span><p>{operatorStatusError || "Loading operator controls…"}</p></div>;
  return <>
    <Shell reviewCount={reviewCount} productionReviewCount={productionReviewCount} scriptClarificationCount={scriptClarificationCount} attentionCount={attentionCount} runner={runner} features={features} search={search} onSearch={setSearch} onUpload={() => setUploadOpen(true)} onLoggedOut={() => { setFeatures(null); setSession("anonymous"); }}>
      <Routes>
        <Route path="/" element={<LibraryPage search={search} refreshKey={refreshKey} onLibrary={captureLibrary} />} />
        <Route path="/ads/:id" element={<AdDetailPage onChanged={refresh} />} />
        <Route path="/brands" element={<BrandsPage />} />
        <Route path="/brands/:slug" element={<BrandDetailPage />} />
        <Route path="/styles" element={<StylesPage />} />
        <Route path="/styles/:id" element={<StyleDetailPage />} />
        <Route path="/make-ad" element={<MakeAdPage variationsEnabled={features.variations} staticAdsEnabled={features.staticAds} staticAdAnimationEnabled={features.staticAdAnimation} scratch3dEnabled={features.scratch3d} suppliedScriptEnabled={features.scratchSuppliedScript} whiteboardEnabled={features.scratchWhiteboard} />} />
        <Route path="/kanban" element={features.kanbanRead ? <ProductionKanbanPage /> : <Navigate to="/make-ad" replace />} />
        <Route path="/kanban/:id" element={features.kanbanRead ? <ProductionReviewPage actionsEnabled={features.kanbanActions} /> : <Navigate to="/make-ad" replace />} />
        <Route path="/kanban/script-clarification/:runId/:id" element={features.kanbanRead ? <ScriptClarificationPage /> : <Navigate to="/make-ad" replace />} />
        <Route path="/strategy-reviews" element={features.scratch3d ? <StrategyReviewsPage /> : <Navigate to="/make-ad" replace />} />
        <Route path="/strategy-reviews/:id" element={features.scratch3d ? <StrategyReviewDetailPage /> : <Navigate to="/make-ad" replace />} />
        <Route path="/clients/calendar" element={features.clientCalendar ? <ClientCalendarPage /> : <Navigate to="/" replace />} />
        <Route path="/landing-pages" element={<PlaceholderPage eyebrow="Brand intelligence" title="Landing pages" copy="Landing-page capture, organization, and analysis will live here." />} />
        <Route path="/quizzes" element={<PlaceholderPage eyebrow="Brand intelligence" title="Quizzes" copy="Quiz capture, organization, and analysis will live here." />} />
        <Route path="/reviews" element={<ReviewsPage onChanged={refresh} />} />
        <Route path="/operations" element={<OperationsPage />} />
        <Route path="/system-visual" element={<SystemVisualPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
    <UploadDrawer open={uploadOpen} onClose={() => setUploadOpen(false)} onQueued={refresh} />
  </>;
}
